import './kitStyles';
import React, { useEffect, useRef } from 'react';
import { setKeyboardInput, setTouchInput } from './inputMode';
import { swallowClick } from './popRules';
import { layerMode, layerRoot, type LayerMode } from './layerScope';

/**
 * WEB-3310 юнит 2а-2 — правила телефона и ввода (эталон `touchInit`,
 * `design/cabinet-rescin/kit.jsx`, владелец 23.10):
 *  - признак «человек работает клавиатурой» (`ds/inputMode.ts`): клавиша без
 *    ⌘/Ctrl/Alt — да, любое `pointerdown` — нет;
 *  - на сенсорном экране (`matchMedia('(pointer: coarse)')`) поле ввода (не
 *    флажок и не радио), получившее фокус не от касания по самому полю или его
 *    `label` (`autoFocus` окна или строки), фокус сразу отдаёт — не приближает
 *    страницу и не поднимает клавиатуру; поле внутри `tr` (переименование
 *    строки, открыто нарочно) — не отдаёт;
 *  - касание мимо открытого поля переименования внутри `tr` гасит следующий
 *    `click` — строка возвращается в обычный вид и никуда не переводит.
 *  - юнит 3 (телефонный слой, эталон `kit.jsx:900–911`, кит 03:233): признак
 *    касания (`pointerdown` не мышью — да, мышью — нет); поле с клавиатурой —
 *    тоже открытое: касание (не мышью) мимо поля в фокусе (не в `tr`) только
 *    убирает клавиатуру — (1) есть открытое окно `dialog[open]` своего корня, не
 *    содержащее поле, — поле теряет фокус, щелчок не гасится (касание принадлежит
 *    окну); (2) цель — другое поле, `select`, список, пункт, меню — ничего;
 *    (3) цель внутри своей обёртки поля (`.ask, .sx-q, .input-wrap, label` или
 *    родитель поля) — ничего; (4) иначе — следующий `click` гасится, поле теряет
 *    фокус.
 * Ставится один раз рядом с `TipLayer`: владельцем корня `.ds` внутри корня (витрина
 * `/kit`) либо оболочкой кабинета вне `.ds` с `everyRoot` (правило — `layerScope.ts`).
 * Обработчики — на `document` в фазе захвата; признак клавиатуры общий, а фокус
 * отдаёт и щелчок гасит только для полей внутри своего корня `.ds` (с `everyRoot` —
 * любого ближайшего `.ds` поля); поля без `.ds` (страницы на antd) не задеваются —
 * решение оркестратора по 2а-1.
 */
const FIELD = 'input:not([type=checkbox]):not([type=radio]), textarea';
/** Поле, держащее клавиатуру (эталон `kit.jsx:902`). */
const KB_FIELD = 'input:not([type=checkbox]):not([type=radio]):not([type=range]), textarea, [contenteditable=true]';
/** Касание сюда — не «мимо поля» (эталон `kit.jsx:908`). */
const NOT_OUT =
  'input:not([type=checkbox]):not([type=radio]), textarea, select, [contenteditable=true], .pop, [role=listbox], [role=option], [role=menu]';
const WRAP = '.ask, .sx-q, .input-wrap, label';

/** Касание мимо поля с клавиатурой своего корня: убрать клавиатуру, щелчок под пальцем — по веткам. */
function tapPastField(mode: LayerMode, a: Element | null, t: Node | null): void {
  const root = layerRoot(a, mode);
  if (!a || !t || !root || !a.matches(KB_FIELD) || a.closest('tr') || a === t || a.contains(t)) return;
  const el = t instanceof Element ? t : t.parentElement;
  if (!el) return;
  const blur = () => (a as HTMLElement).blur();
  const dlg = Array.from(root.querySelectorAll('dialog[open]')).pop();
  if (dlg && !dlg.contains(a)) {
    blur();
    return;
  }
  if (el.closest(NOT_OUT)) return;
  const own = a.closest(WRAP) || a.parentElement;
  if (own && own.contains(el)) return;
  swallowClick();
  blur();
}

export function TouchRules({ everyRoot = false }: { everyRoot?: boolean } = {}): React.ReactElement {
  const mark = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const mode = layerMode(mark.current, everyRoot);
    if (!mode) return undefined;
    let downAt: EventTarget | null = null;
    const key = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey && !e.altKey) setKeyboardInput(true);
    };
    const down = (e: Event) => {
      setKeyboardInput(false);
      const touch = (e as PointerEvent).pointerType !== 'mouse';
      setTouchInput(touch);
      downAt = e.target;
      const a = document.activeElement;
      const t = e.target instanceof Node ? e.target : null;
      if (a && layerRoot(a, mode) && a.matches(FIELD) && a.closest('tr') && a !== t && !(t && a.contains(t))) {
        swallowClick();
        return;
      }
      if (touch) tapPastField(mode, a, t);
    };
    const focus = (e: Event) => {
      const el = e.target;
      if (!(el instanceof HTMLElement) || !layerRoot(el, mode)) return;
      if (typeof window.matchMedia !== 'function' || !window.matchMedia('(pointer: coarse)')?.matches) return;
      if (!el.matches(FIELD) || el.closest('tr')) return;
      const lab = downAt instanceof Element ? downAt.closest('label') : null;
      if (downAt === el || (lab && lab.contains(el))) return;
      el.blur();
    };
    document.addEventListener('keydown', key, true);
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('focusin', focus, true);
    return () => {
      document.removeEventListener('keydown', key, true);
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('focusin', focus, true);
    };
  }, [everyRoot]);
  return <span ref={mark} hidden />;
}
